import { useParams, Link, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { calculatorsByCategory, CATEGORIES } from '@/registry'
import { CalculatorCard } from '@/components/ui/CalculatorCard'
import { buildLocaleSeoLinks } from '@/i18n/seo'
import { resolveLocaleFromPath, toCurrentLocalePath } from '@/i18n/routing'
import { getCategoryContent, getCategoryPageCopy } from './content'

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://toolvance.com'

export function CategoryPage() {
  const { pathname } = useLocation()
  const { category } = useParams<{ category: string }>()
  const { locale } = resolveLocaleFromPath(pathname)
  const pageCopy = getCategoryPageCopy(locale)
  const localize = (path: string) => toCurrentLocalePath(pathname, path)
  const cat = CATEGORIES.find(c => c.id === category)
  const calcs = calculatorsByCategory[category || ''] || []
  const content = getCategoryContent(locale, category || '')

  if (!cat) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{pageCopy.notFound}</h1>
        <Link to={localize('/')} className="text-brand-600 hover:text-brand-700">{'<-'} {pageCopy.backHome}</Link>
      </div>
    )
  }

  const isCalcGroup = (cat as { group?: string }).group === 'calculators'
  const fallbackLabel = isCalcGroup ? `${cat.label} Calculators` : cat.label
  const h1 = content?.h1 ?? `${cat.icon} ${fallbackLabel}`
  const title = content?.seoTitle ?? `${fallbackLabel} - ${pageCopy.fallbackToolsTitle}`
  const description = content?.seoDescription ?? `${calcs.map(c => c.name).slice(0, 4).join(', ')} and more.`
  const intro = content?.intro ?? description
  const seoLinks = buildLocaleSeoLinks(pathname || `/${category}`, SITE_URL)

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: seoLinks.canonicalUrl,
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={seoLinks.canonicalUrl} />
        {seoLinks.alternates.map(alt => (
          <link key={alt.locale} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={seoLinks.xDefaultUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={seoLinks.canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
          <Link to={localize('/')} className="hover:text-brand-600">{pageCopy.breadcrumbHome}</Link>
          <span>/</span>
          <span className="text-gray-800 font-medium">{cat.label}</span>
        </nav>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">{h1}</h1>
        <p className="text-gray-600 mb-8 leading-relaxed max-w-3xl">{intro}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {calcs.map(c => <CalculatorCard key={c.slug} calc={c} />)}
        </div>
      </div>
    </>
  )
}
